import { useCallback, useMemo, useState } from 'react';

import { Button } from '../../Button';
import { Box } from '../../Box';
import { Dialog, DialogContent } from '../../Dialog';
import { FileInput, FileInputProps, FileInputValue } from '../FileInput';
import { RadioInput } from '../RadioInput';
import { ImageCropper } from '../../ImageCropper';
import { Typography } from '../../Typography';

const DEFAULT_ASPECT_RATIO_VALUE = '16:9';
const DEFAULT_ASPECT_RATIO = 16 / 9;

const getBlobDimensions = (
  blob: Blob,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(blob);
  });
};

export interface FileInputWithCropProps extends FileInputProps {
  sizeAspect?: number;
  dialogTitle?: string;
  dialogDescription?: string;
}

export const FileInputWithCrop = ({
  onChange,
  cache,
  validators,
  aspectRatioOptions,
  dialogTitle = 'Кадрирование обложки',
  dialogDescription = 'Выделите нужную область изображения',
  sizeAspect,
  testId = 'test-id-file-input',
  ...rest
}: FileInputWithCropProps) => {
  const [cropModalState, setCropModalState] = useState<boolean>(false);
  const [blobDocument, setBlobDocument] = useState<FileInputValue | undefined>(
    undefined,
  );
  const [croppedResult, setCroppedResult] = useState<Blob | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [cropError, setCropError] = useState<string | null>(null);

  const defaultAspectRatio = useMemo<string>(
    () => aspectRatioOptions?.[0]?.value || DEFAULT_ASPECT_RATIO_VALUE,
    [aspectRatioOptions],
  );

  const [selectedAspectRatio, setSelectedAspectRatio] =
    useState<string>(defaultAspectRatio);

  const handleAspectRatioChange = (value: string | number) => {
    setSelectedAspectRatio(String(value));
    setCropError(null);
  };

  const currentAspectRatioOption = useMemo(() => {
    if (aspectRatioOptions && aspectRatioOptions.length > 0) {
      return aspectRatioOptions.find(
        (opt) => opt.value === selectedAspectRatio,
      );
    }
    return undefined;
  }, [aspectRatioOptions, selectedAspectRatio]);

  const currentAspectRatio = useMemo(() => {
    if (currentAspectRatioOption) {
      return currentAspectRatioOption.ratio;
    }
    if (typeof sizeAspect === 'number') {
      return sizeAspect;
    }
    return DEFAULT_ASPECT_RATIO;
  }, [currentAspectRatioOption, sizeAspect]);

  const currentMinDimensions = useMemo(() => {
    if (currentAspectRatioOption) {
      return {
        minWidth: currentAspectRatioOption.minWidth,
        minHeight: currentAspectRatioOption.minHeight,
      };
    }
    return { minWidth: undefined, minHeight: undefined };
  }, [currentAspectRatioOption]);

  const validateCropDimensions = useCallback(
    async (blob: Blob): Promise<string | null> => {
      const { minWidth, minHeight } = currentMinDimensions;
      if (!minWidth && !minHeight) {
        return null;
      }

      const { width, height } = await getBlobDimensions(blob);

      if (minWidth && width < minWidth) {
        return `Размер изображения не может быть меньше ${minWidth} x ${minHeight}`;
      }
      if (minHeight && height < minHeight) {
        return `Размер изображения не может быть меньше ${minWidth} x ${minHeight}`;
      }

      return null;
    },
    [currentMinDimensions],
  );

  const handleCropModalState =
    (state: boolean, reset = true) =>
    () => {
      setCropModalState(state);
      setCropError(null);
      if (!state && reset) {
        setCroppedResult(null);
        setBlobDocument(undefined);
        onChange(undefined);
      }
    };

  const handleCropComplete = (croppedBlob: Blob) => {
    setCroppedResult(croppedBlob);
    setCropError(null);
  };

  const convertToFormat = (blob: Blob, mimeType: string): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx?.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
        canvas.toBlob(
          (resultBlob) => {
            resolve(resultBlob || blob);
          },
          mimeType,
          0.9,
        );
      };

      img.src = URL.createObjectURL(blob);
    });
  };

  const handleApplyCrop = async () => {
    if (!croppedResult) return;

    const validationError = await validateCropDimensions(croppedResult);
    if (validationError) {
      setCropError(validationError);
      return;
    }

    setLoading(true);
    setCropError(null);

    let originalFileName = 'image';
    let originalFileType = 'image/jpeg';

    if (
      blobDocument &&
      blobDocument.source &&
      typeof blobDocument.source !== 'string' &&
      blobDocument.source instanceof File
    ) {
      const fileObj = blobDocument.source;

      originalFileType = fileObj.type || 'image/jpeg';
      if (fileObj.name) {
        originalFileName = fileObj.name;
      }
    }

    let extension = '';

    if (originalFileType && originalFileType.includes('/')) {
      extension = originalFileType.split('/')[1];
    }

    let fileNameWithExt = originalFileName;
    if (originalFileName.lastIndexOf('.') > 0) {
      fileNameWithExt = `${originalFileName.substring(
        0,
        originalFileName.lastIndexOf('.'),
      )}.${extension}`;
    } else {
      fileNameWithExt = `${originalFileName}.${extension}`;
    }

    const targetMimeType = originalFileType;

    const resultBlob = await convertToFormat(croppedResult, targetMimeType);

    const file = new File([resultBlob], fileNameWithExt, {
      type: targetMimeType,
    });

    if (cache) {
      try {
        const { file_id: fileId, expired_at: expiredAt } = await cache(file);
        onChange({
          source: file,
          value: JSON.stringify({
            type: 'file_cache',
            id: fileId,
            expiredAt: expiredAt,
          }),
        });
        setLoading(false);
        handleCropModalState(false, false)();
      } catch (error) {
        setLoading(false);
        handleCropModalState(false)();
        return;
      }
    } else {
      handleCropModalState(false, false)();
      return;
    }
  };

  const handleOnChange = async (value: FileInputValue | undefined) => {
    setBlobDocument(value);

    if (!value) {
      onChange(undefined);
      return;
    }

    if (validators && (await validators(value))) return;

    if (
      value.source &&
      typeof value.source !== 'string' &&
      value.source instanceof File
    ) {
      handleCropModalState(true)();
    }
  };

  const formattedBlob = useMemo(() => {
    return new Blob(
      [
        JSON.stringify({
          url: URL.createObjectURL(
            blobDocument?.source instanceof File
              ? blobDocument.source
              : new Blob(),
          ),
        }),
      ],
      { type: 'application/json' },
    );
  }, [blobDocument?.source]);

  return (
    <Box>
      <FileInput
        {...rest}
        testId={testId}
        onChange={handleOnChange}
        variant="input"
        validators={validators}
      />

      <Dialog open={cropModalState} onClose={handleCropModalState(false)}>
        <DialogContent
          onClose={handleCropModalState(false)}
          actions={
            <>
              <Button
                variant="tertiaryLight"
                size="large"
                onClick={handleCropModalState(false)}
                testId={`${testId}-crop-cancel`}
              >
                Отменить
              </Button>
              <Button
                variant="primary"
                size="large"
                loading={loading}
                disabled={loading}
                onClick={handleApplyCrop}
                testId={`${testId}-crop-save`}
              >
                Сохранить
              </Button>
            </>
          }
        >
          <Box display="flex" flexDirection="column" gap="16">
            <Typography variant="h5">{dialogTitle}</Typography>
            {aspectRatioOptions && aspectRatioOptions.length > 1 && (
              <RadioInput
                row
                withoutMargins
                items={aspectRatioOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
                value={selectedAspectRatio}
                onChange={handleAspectRatioChange}
              />
            )}
            <Typography variant="body1" color="text.light.secondary">
              {dialogDescription}
            </Typography>
            <Box display="flex" minHeight={300} width="100%">
              <ImageCropper
                blob={formattedBlob}
                sizeAspect={currentAspectRatio}
                onCropComplete={handleCropComplete}
              />
            </Box>
            {cropError && (
              <Box testId={`${testId}-crop-error`}>
                <Typography variant="body2" color="accent.text.error">
                  {cropError}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
